import React from "react";
import { useTranslation } from "react-i18next";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import ProductSelector from "./ProductSelector";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import Div from "@jumbo/shared/Div";
import { CROSS_SELL_PLACEMENTS } from "app/utils/constants/settings";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DragIndicator from "@mui/icons-material/DragIndicator";
import { getProducts } from "app/services/api/product";
import { updateCategoryCrossSell } from "app/services/api/category";
import { useSnackbar } from "notistack";

const CategoryCrossSellTab = ({ category, products }) => {
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const lang = i18n.language || "en";
  const crossSell = category?.crossSell || {};
  const resolveText = (val) => {
    if (val == null) return "";
    if (typeof val === "string" || typeof val === "number") return String(val);
    if (typeof val === "object") {
      return (
        val[lang] ??
        val.en ??
        val.sk ??
        val.cs ??
        val.de ??
        String(Object.values(val)[0] ?? "")
      );
    }
    return String(val);
  };
  const placementsOptions = CROSS_SELL_PLACEMENTS;
  const [settings, setSettings] = React.useState({
    maxRecommendations:
      Number(crossSell?.globalSettings?.maxRecommendations) || 0,
    placements: crossSell?.globalSettings?.placements || [],
    fallbackStrategy: crossSell?.globalSettings?.fallbackStrategy || "none",
    enabled: (crossSell?.globalSettings?.status || "active") === "active",
  });

  React.useEffect(() => {
    setSettings({
      maxRecommendations:
        Number(crossSell?.globalSettings?.maxRecommendations) || 0,
      placements: crossSell?.globalSettings?.placements || [],
      fallbackStrategy: crossSell?.globalSettings?.fallbackStrategy || "none",
      enabled: (crossSell?.globalSettings?.status || "active") === "active",
    });
  }, [
    category,
    crossSell?.globalSettings?.maxRecommendations,
    crossSell?.globalSettings?.placements,
    crossSell?.globalSettings?.fallbackStrategy,
    crossSell?.globalSettings?.status,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rulesWithNames = (rules || []).map((r) => ({
      ...r,
      name: typeof r.name === "string" ? r.name : resolveText(r.name || ""),
    }));
    const payload = {
      globalSettings: {
        maxRecommendations: Number(settings.maxRecommendations || 0),
        placements: settings.placements || [],
        fallbackStrategy: settings.fallbackStrategy || "none",
        status: settings.enabled ? "active" : "inactive",
      },
      rules: rulesWithNames.map((r) => ({
        ...r,
        limit: Number(r.limit || 0),
        placements: r.placements || [],
        products: (r.products || []).map((p) => ({
          id: p.id,
          name: p.name,
          price: p.price,
        })),
      })),
    };
    try {
      await updateCategoryCrossSell(category?._id, payload);
      enqueueSnackbar(t("pages.crossSell.updatedSuccessfully"), {
        variant: "success",
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to update cross sell", err);
      enqueueSnackbar(t("pages.crossSell.updateFailed"), { variant: "error" });
    }
  };

  // Drag-and-drop sortable rules
  const [rules, setRules] = React.useState(crossSell?.rules || []);
  // preserve focus while editing rule names
  const nameInputRefs = React.useRef({});
  const [focusedRuleNameId, setFocusedRuleNameId] = React.useState(null);
  React.useEffect(() => {
    if (!focusedRuleNameId) return;
    const el = nameInputRefs.current[focusedRuleNameId];
    if (el && document.activeElement !== el) {
      // refocus and keep caret at the end
      const len = typeof el.value === "string" ? el.value.length : 0;
      el.focus();
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  }, [rules, focusedRuleNameId]);
  const [productPicker, setProductPicker] = React.useState({
    open: false,
    ruleIndex: null,
  });
  const [confirmRemove, setConfirmRemove] = React.useState({
    open: false,
    index: null,
  });
  // rule names are controlled in state; refs are used only to preserve focus
  const { getAuthUser } = useJumboAuth();
  const authUser = getAuthUser() || {};
  const customerId = authUser?.customerId;
  const [availableProducts, setAvailableProducts] = React.useState([]);
  const productSelectorProducts = React.useMemo(() => {
    const list = products || [];
    return list.map((p) => ({ ...p, _id: p._id || p.id }));
  }, [products]);
  React.useEffect(() => {
    const normalizedRules = (crossSell?.rules || []).map((r) => ({
      ...r,
      name: resolveText(r?.name || ""),
      products: (r?.products || []).map((p) => ({
        ...p,
        name: resolveText(p?.name || ""),
      })),
    }));
    setRules(normalizedRules);
  }, [category, crossSell?.rules]);

  // Load products when modal opens (fallback to backend if crossSell doesn't provide them)
  React.useEffect(() => {
    if (!productPicker.open) return;
    if (availableProducts.length > 0) return;
    let mounted = true;
    (async () => {
      try {
        const response = await getProducts(customerId);
        const { products } = response || {};
        if (products && products !== "no products" && mounted) {
          const arr = Object.keys(products || {}).map((id) => ({
            ...products[id],
            _id: id,
            id,
          }));
          setAvailableProducts(arr);
        }
      } catch (e) {
        setAvailableProducts([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [productPicker.open, customerId, availableProducts.length]);

  const moveRule = React.useCallback((dragIndex, hoverIndex) => {
    setRules((prev) => {
      const updated = [...prev];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, removed);
      return updated;
    });
  }, []);

  const RULE_TYPE = "CROSS_SELL_RULE";
  const PRODUCT_TYPE = "CROSS_SELL_PRODUCT";

  const removeRuleProduct = React.useCallback(
    (ruleId, productId, ruleIndex) => {
      setRules((prev) => {
        const updated = [...prev];
        const idx = ruleIndex ?? updated.findIndex((r) => r.ruleId === ruleId);
        if (idx < 0) return updated;
        const rule = updated[idx] || {};
        const products = (rule.products || []).filter(
          (p) => p.id !== productId
        );
        updated[idx] = { ...rule, products };
        return updated;
      });
    },
    []
  );

  const updateRuleAt = React.useCallback((index, patch) => {
    setRules((prev) => {
      const updated = [...prev];
      const current = updated[index] || {};
      const newRule = { ...current, ...patch };
      updated[index] = newRule;
      return updated;
    });
  }, []);

  const addNewRule = () => {
    const newRule = {
      ruleId: `rule-${Date.now()}`,
      name: "",
      enabled: true,
      limit: 0,
      placements: [],
      products: [],
    };
    setRules((prev) => [newRule, ...(prev || [])]);
  };

  const RuleCard = ({ rule, index }) => {
    const ref = useRef(null);
    const dragHandleRef = useRef(null);
    const [, drop] = useDrop({
      accept: RULE_TYPE,
      hover(item, monitor) {
        if (!ref.current) return;
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;
        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
        moveRule(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });
    const [{ isDragging }, drag] = useDrag({
      type: RULE_TYPE,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });
    // Make entire card a drop target, but only the handle a drag source
    drop(ref);
    drag(dragHandleRef);

    const moveProduct = React.useCallback(
      (dragIndex, hoverIndex) => {
        setRules((prev) => {
          const updated = [...prev];
          const r = updated[index] || {};
          const list = [...(r.products || [])];
          const [moved] = list.splice(dragIndex, 1);
          list.splice(hoverIndex, 0, moved);
          updated[index] = { ...r, products: list };
          return updated;
        });
      },
      [index]
    );

    const ProductRow = ({ product, productIndex }) => {
      const rowRef = useRef(null);
      const rowHandleRef = useRef(null);
      const [, rowDrop] = useDrop({
        accept: PRODUCT_TYPE,
        hover(item, monitor) {
          if (!rowRef.current) return;
          const dragIndex = item.productIndex;
          const hoverIndex = productIndex;
          if (dragIndex === hoverIndex) return;
          const hoverBoundingRect = rowRef.current.getBoundingClientRect();
          const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          const clientOffset = monitor.getClientOffset();
          const hoverClientY = clientOffset.y - hoverBoundingRect.top;
          if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
          if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
          moveProduct(dragIndex, hoverIndex);
          item.productIndex = hoverIndex;
        },
      });
      const [{ isDragging: isRowDragging }, rowDrag] = useDrag({
        type: PRODUCT_TYPE,
        item: { productIndex },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      });
      rowDrop(rowRef);
      rowDrag(rowHandleRef);
      return (
        <Box
          ref={rowRef}
          sx={{
            p: 1,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            opacity: isRowDragging ? 0.6 : 1,
            mb: 1,
          }}
        >
          <IconButton
            ref={rowHandleRef}
            aria-label="drag product"
            sx={{ cursor: "grab" }}
          >
            <DragIndicator sx={{ color: "text.secondary", fontSize: 20 }} />
          </IconButton>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            {resolveText(product.name)}
          </Typography>
          <IconButton
            aria-label="remove product"
            onClick={() => removeRuleProduct(rule.ruleId, product.id, index)}
          >
            <DeleteForeverIcon sx={{ color: "#000" }} />
          </IconButton>
        </Box>
      );
    };

    const titleValue = rule?.name || "";
    return (
      <Box
        ref={ref}
        sx={{
          mt: 3,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          opacity: isDragging ? 0.6 : 1,
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1,
            bgcolor: "action.hover",
            borderRadius: 1,
          }}
        >
          <IconButton
            ref={dragHandleRef}
            size="small"
            aria-label="drag rule"
            sx={{ cursor: "grab" }}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
          <FormControlLabel
            control={
              <Switch
                checked={!!rule.enabled}
                onChange={(e) =>
                  updateRuleAt(index, { enabled: e.target.checked })
                }
              />
            }
            label={t("pages.crossSell.enabled")}
            sx={{ m: 0, ml: 1 }}
          />
          <TextField
            size="small"
            label={t("pages.crossSell.ruleName")}
            value={titleValue}
            onChange={(e) => updateRuleAt(index, { name: e.target.value })}
            onFocus={(e) => setFocusedRuleNameId(rule.ruleId)}
            inputRef={(el) => {
              if (el) nameInputRefs.current[rule.ruleId] = el;
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 160 }}
          />
          <TextField
            type="number"
            size="small"
            label={t("pages.crossSell.limit")}
            value={Number(rule.limit || 0)}
            onChange={(e) =>
              updateRuleAt(index, { limit: Number(e.target.value || 0) })
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0 }}
            sx={{ width: 120 }}
          />
          <TextField
            select
            size="small"
            label={t("pages.crossSell.placements")}
            value={rule.placements || []}
            onChange={(e) => {
              const val = e.target.value;
              const next = Array.isArray(val)
                ? val
                : String(val || "")
                    .split(",")
                    .map((v) => v.trim())
                    .filter(Boolean);
              updateRuleAt(index, { placements: next });
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected || []).join(", "),
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 220, maxWidth: 320 }}
          >
            {placementsOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {t(`pages.dsokCategories.crossSellPlacements.${opt}`)}
              </MenuItem>
            ))}
          </TextField>
          <IconButton
            size="small"
            aria-label="remove rule"
            onClick={() => setConfirmRemove({ open: true, index })}
          >
            <DeleteForeverIcon sx={{ color: "#000" }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("pages.crossSell.productsTitle")}
            </Typography>
            {(rule?.products || []).map((product, pIdx) => (
              <ProductRow
                key={product.id}
                product={product}
                productIndex={pIdx}
              />
            ))}
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                onClick={() =>
                  setProductPicker({ open: true, ruleIndex: index })
                }
              >
                {t("buttons.addProduct")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, width: "100%", height: "100vh", overflow: "auto" }}>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        <Alert variant="outlined" severity="info">
          {t("pages.dsokCategories.addCrossSellInfo")}
        </Alert>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <Alert variant="outlined" severity="info">
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>
                {t("pages.dsokCategories.crossSellRuleNameLabel")}
              </strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t("pages.dsokCategories.crossSellLimitLabel")}:</strong>{" "}
              {t("pages.dsokCategories.crossSellLimitHelp")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>
                {t("pages.dsokCategories.crossSellPlacementLabel")}
              </strong>
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <li>
                {t(
                  "pages.dsokCategories.crossSellPlacements.after_adding_product_to_cart"
                )}
              </li>
              <li>
                {t("pages.dsokCategories.crossSellPlacements.checkout_page")}
              </li>
              <li>
                {t(
                  "pages.dsokCategories.crossSellPlacements.product_detail_page"
                )}
              </li>
            </Box>
          </Box>
        </Alert>
      </Typography>
      <Div
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
              gap: 2,
              alignItems: "center",
              maxWidth: 900,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.enabled}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, enabled: e.target.checked }))
                  }
                />
              }
              label={t("pages.crossSell.enabled")}
              sx={{ m: 0 }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button type="submit" variant="contained" sx={{ ml: "auto" }}>
                {t("buttons.save")}
              </Button>
            </Box>
          </Box>
          <Box
            sx={{ borderTop: 1, borderColor: "divider", mt: 2, pt: 2, mb: 2 }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={addNewRule}
            >
              {t("pages.crossSell.addRule")}
            </Button>
          </Box>
          {rules.map((rule, idx) => (
            <RuleCard key={rule.ruleId || idx} rule={rule} index={idx} />
          ))}
        </Box>
      </Div>
      <Modal
        open={productPicker.open}
        onClose={() => setProductPicker({ open: false, ruleIndex: null })}
      >
        <Div
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "75vw",
            maxHeight: "70vh",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          <ProductSelector
            setFieldValue={(field, value) => {
              if (field === "products") {
                const idx = productPicker.ruleIndex;
                setRules((prev) => {
                  const updated = [...prev];
                  const rule = updated[idx] || {};
                  const existing = [...(rule.products || [])];
                  const toAdd = Object.keys(value || {}).map((id) => {
                    const prod = value[id] || {};
                    return {
                      id,
                      name: resolveText(
                        prod.productName ||
                          prod.name ||
                          prod.metadata?.name ||
                          ""
                      ),
                      price: prod.metadata?.price || prod.price || 0,
                    };
                  });
                  toAdd.forEach((prod) => {
                    if (!existing.find((x) => x.id === prod.id)) {
                      existing.push(prod);
                    }
                  });
                  updated[idx] = { ...rule, products: existing };
                  return updated;
                });
              }
            }}
            values={{}}
            setCloseModal={() =>
              setProductPicker({ open: false, ruleIndex: null })
            }
            categoryId={category?._id}
            products={
              Array.isArray(availableProducts) && availableProducts.length
                ? availableProducts
                : Array.isArray(productSelectorProducts)
                ? productSelectorProducts
                : []
            }
          />
        </Div>
      </Modal>
      <Dialog
        open={confirmRemove.open}
        onClose={() => setConfirmRemove({ open: false, index: null })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("widgets.confirmDialog.areYouSure")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("pages.dsokCategories.removeRuleConfirm", {
              defaultValue: "Do you really want to remove this rule?",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmRemove({ open: false, index: null })}
          >
            {t("buttons.no")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setRules((prev) =>
                prev.filter((_, i) => i !== confirmRemove.index)
              );
              setConfirmRemove({ open: false, index: null });
            }}
          >
            {t("buttons.yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryCrossSellTab;
